<template>
  <ion-page>
    <ion-header mode="ios" class="ion-no-border">
      <ion-toolbar class="px-1 toolbar-title">
        <ion-buttons class="mr-2" slot="start">
          <ion-back-button
            color="primary"
            mode="ios"
            text=""
            :default-href="
              userType === 'b2b'
                ? `/b2b/order/purchase/order-detail/${orderId}`
                : `/sale/order/purchase/order-detail/${orderId}`
            "
            @click="goBack"
            :icon="chevronBackOutline"
          ></ion-back-button>
        </ion-buttons>
        <ion-label class="head-title">{{ setOrderBookingHeader }} #{{ orderId }}</ion-label>
      </ion-toolbar>
      <div class="grey-lines" />
    </ion-header>
    <ion-content>
      <ion-refresher slot="fixed" @ionRefresh="handleRefresh($event)">
        <ion-refresher-content></ion-refresher-content>
      </ion-refresher>
      <!-- show pull refresh page when no internet connection -->
      <pull-refresh v-if="isConnectionIssue"></pull-refresh>
      <div :class="!isConnectionIssue ? 'bg-content' : ''">
        <!-- Skeleton loading -->
        <div v-if="showSkeletonLoading" class="column mx-3">
          <ion-skeleton-text
            class="my-2"
            animated
            style="width: 50%; height: 20px; border-radius: 6px"
          ></ion-skeleton-text>
          <ion-row class="ion-align-items-start mb-1">
            <ion-col class="mr-3 ion-no-padding" size="auto">
              <ion-skeleton-text
                animated
                style="width: 16px; height: 16px; border-radius: 100%"
              ></ion-skeleton-text>
            </ion-col>
            <ion-col class="ion-no-padding">
              <div class="column">
                <ion-skeleton-text
                  animated
                  style="width: 80%; height: 14px; border-radius: 4px"
                ></ion-skeleton-text>
                <ion-skeleton-text
                  animated
                  style="width: 50%; height: 14px; border-radius: 4px"
                ></ion-skeleton-text>
                <ion-skeleton-text
                  animated
                  style="width: 100%; height: 56px; border-radius: 10px"
                ></ion-skeleton-text>
              </div>
            </ion-col>
          </ion-row>
          <div v-for="(item, index) in 3" :key="index">
            <ion-row class="ion-align-items-start mb-1">
              <ion-col class="mr-3 ion-no-padding" size="auto">
                <ion-skeleton-text
                  animated
                  style="width: 16px; height: 16px; border-radius: 100%"
                ></ion-skeleton-text>
              </ion-col>
              <ion-col class="ion-no-padding">
                <div class="column">
                  <ion-skeleton-text
                    animated
                    style="width: 80%; height: 14px; border-radius: 4px"
                  ></ion-skeleton-text>
                  <ion-skeleton-text
                    animated
                    style="width: 50%; height: 14px; border-radius: 4px"
                  ></ion-skeleton-text>
                </div>
              </ion-col>
            </ion-row>
          </div>
        </div>
        <div v-else class="bg-white">
          <div v-if="orderStatusLogs && !isOrderIdNotFound && !isConnectionIssue">
            <order-status-cheap
              :orderStatusDetail="orderStatusLogs.logs"
              :lastOrderStatus="orderStatusLogs.last_order_log_info.status"
              :parent="'trackOrder'"
              :isBookingOrder="isBookingOrder"
              :userType="userType"
              :orderId="orderId"
            />
            <div class="mb-2"></div>
            <!-- Order status logs info -->
            <div v-for="(item, index) in orderStatusLogs.logs" :key="index">
              <ion-row>
                <ion-col size="auto" class="mr-2 column center-items">
                  <div v-show="index === 0" class="time-line" :class="timeLineDot(item.status)" />
                  <div
                    v-show="orderStatusLogs?.logs?.length > 1 && index === 0"
                    class="track-line-on"
                    :class="timeLine(item.status)"
                  />

                  <!-- grey or of timeline dot -->
                  <div v-show="index > 0" class="time-line time-line-grey" />
                  <div
                    v-show="index > 0 && index !== orderStatusLogs?.logs?.length - 1"
                    class="track-line-off"
                  />
                </ion-col>

                <ion-col class="column">
                  <ion-text
                    class="mb-1 mini-info"
                    :class="index === 0 ? orderInfoColor(item.status) : 'title-grey'"
                    >{{ orderStatusInfo(item) }}</ion-text
                  >
                  <ion-text v-if="showAmended(item.status)" class="mb-1 mini-info text-grey">{{
                    $t('updated_by') + updatedBy(item)
                  }}</ion-text>
                  <ion-text class="text-grey mini-info">{{ lastUpdateDateTime(item.created_at) }} </ion-text>

                  <!-- Updated items detail section -->
                  <div v-if="showAmended(item.status)" class="my-2">
                    <UpdateDetailSection
                      :amendedItem="item.amends"
                      :deliveryAmend="item.delivery_info_changed"
                      :deliveryDate="orderStatusLogs?.delivery_info.delivery_date"
                      :deliveryTime="orderStatusLogs?.delivery_info.delivery_time"
                      :deliveryAddress="orderStatusLogs?.delivery_info.delivery_address"
                      :userCurrency="userInfo?.currency_symbol"
                    />
                  </div>
                  <!-- order status reason or note -->
                  <div
                    v-if="showOrderStatusNoteReason(item)"
                    class="px-2 py-2 po-number-and-note-reason mt-2"
                  >
                    <div class="column">
                      <ion-text class="po-text mb-1 mini-info"> {{ titleNoteReason(item.status) }}</ion-text>
                      <ion-text class="mini-info"> {{ noteAndReason(item) }}</ion-text>
                    </div>
                  </div>
                  <!-- View driver map location button -->
                  <ion-item
                    v-if="
                      showDriverMapLocation(item.status) &&
                      index === 0 &&
                      item.tracking_link &&
                      !checkEmptyLink(item.tracking_link)
                    "
                    class="view-driver-location-btn"
                    button
                    detail="false"
                    lines="none"
                    @click="setTrackDriverLocation(true, item.tracking_link)"
                  >
                    <ion-icon
                      class="mr-2"
                      style="width: 24px; height: 24px"
                      :icon="'/assets/images/map-location.svg'"
                    />
                    <ion-text class="text-primary">{{ $t('view_driver_location') }}</ion-text>
                  </ion-item>

                  <!-- View image button -->
                  <ion-button
                    v-if="item.images?.length > 0"
                    class="mt-3 track-order-btn"
                    expand="block"
                    fill="clear"
                    @click="setOpenImages(true, item.images)"
                  >
                    <ion-icon class="mr-2" style="width: 24px; height: 24px" :icon="eyeOutline" />
                    {{ $t('view_image') }}
                  </ion-button>
                </ion-col>
              </ion-row>
            </div>
          </div>
          <!-- show not found order -->
          <connection-and-order-not-found
            v-if="isOrderIdNotFound"
            :isOrderIdNotFound="isOrderIdNotFound"
            :userType="userType"
          />
        </div>
      </div>
    </ion-content>

    <!-- Track driver location -->
    <ion-modal :is-open="isOpenDriverLocation" :backdropDismiss="false" @didDismiss="closeTrackDriverPage">
      <track-driver-location
        :trackingLink="trackingLinkUrl"
        :userType="userType"
        :orderId="orderId"
        @close-page="closeTrackDriverPage"
      ></track-driver-location>
    </ion-modal>

    <!-- Preview images -->
    <ion-modal :is-open="isOpenPreviewImages" :backdropDismiss="false" @didDismiss="setOpenImages(false, [])">
      <preview-images
        :images="imagesData"
        :userType="userType"
        @close-page="setOpenImages(false, [])"
      ></preview-images>
    </ion-modal>
  </ion-page>
</template>
<script>
import { apolloClient } from '@/main';
import { ACTION_BY_MERCHANT, ORDER_STATUS_NAME, USER_TYPE } from '@/modules/shared/constants';
import { getOrderStatusLogs } from '@/modules/shared/services/graphql';
import { useBackButton } from '@ionic/vue';
import dayjs from 'dayjs';
import { chevronBackOutline, eyeOutline } from 'ionicons/icons';
import { computed, defineAsyncComponent, defineComponent, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute, useRouter } from 'vue-router';

export default defineComponent({
  components: {
    // components
    OrderStatusCheap: defineAsyncComponent(() => import('./components/OrderStatusCheap.vue')),
    ConnectionAndOrderNotFound: defineAsyncComponent(() =>
      import('./components/ConnectionIssueAndOrderNotFound.vue')
    ),
    TrackDriverLocation: defineAsyncComponent(() => import('./TrackDriverLocation.vue')),
    PreviewImages: defineAsyncComponent(() => import('./components/PreviewImage.vue')),
    UpdateDetailSection: defineAsyncComponent(() => import('./components/UpdateDetailSection.vue')),
    PullRefresh: defineAsyncComponent(() => import('@/modules/shared/views/refreshPage/PullRefresh.vue'))
  },
  inject: ['$storage'],

  setup() {
    const route = useRoute();
    const router = useRouter();
    const { t } = useI18n();
    const orderStatusLogs = ref(null);
    const userInfo = ref(null);
    const showSkeletonLoading = ref(true);
    const setSkeletonloading = (state) => (showSkeletonLoading.value = state);
    const orderId = ref('');
    const userType = ref('');
    const isConnectionIssue = ref(false);
    const isOrderIdNotFound = ref(false);
    const isOpenDriverLocation = ref(false);
    const trackingLinkUrl = ref('');
    const setTrackDriverLocation = (state, url) => {
      isOpenDriverLocation.value = state;
      trackingLinkUrl.value = url;
      if (state) {
        isBackFromTrackDriver.value = false;
      }
    };
    const isOpenPreviewImages = ref(false);
    const imagesData = ref([]);
    const setOpenImages = (state, images) => (
      (isOpenPreviewImages.value = state), (imagesData.value = images)
    );
    const isBooking = ref(false);

    const isBackFromTrackDriver = ref(false);
    const clearIframeHistoryStateEffect = () => {
      isBackFromTrackDriver.value = false;
      // the <iframe> used inside TrackDriverLocation.vue is effected the history state back and add 1 more state history
      // so now need to use back with 2 step to clear it
      router.go(-2);
    };
    const closeTrackDriverPage = () => {
      setTrackDriverLocation(false, '');
      isBackFromTrackDriver.value = true;
    };

    const goBack = () => {
      if (isBackFromTrackDriver.value) {
        clearIframeHistoryStateEffect();
      } else {
        router.back();
      }
    };

    // handle when user use hardware back button
    useBackButton(10, () => {
      if (isOpenDriverLocation.value) {
        closeTrackDriverPage();
      } else if (isOpenPreviewImages.value) {
        setOpenImages(false, []);
      } else {
        goBack();
      }
    });

    const getOrderLogsData = async (orderId) => {
      setSkeletonloading(true);
      const currentPathUrl = window.location.pathname;
      const pathSegments = currentPathUrl.split('/');
      userType.value = pathSegments[1];

      try {
        const orderIdNumber = parseInt(orderId);
        const { data, errors } = await apolloClient.query({
          query: getOrderStatusLogs,
          variables: {
            orderId: orderIdNumber
          }
        });

        if (errors && errors.length > 0) {
          // when order is not found
          const errorMessage = errors[0].message;
          isOrderIdNotFound.value = errorMessage === 'Order not found';
        } else {
          orderStatusLogs.value = data.getOrderStatusLogs;
        }
      } catch (error) {
        isConnectionIssue.value =
          error.message === t('load_failed') || error.message === t('failed_to_fetch');
      } finally {
        setSkeletonloading(false);
      }
    };

    const handleRefresh = async (event) => {
      isConnectionIssue.value = false;
      setSkeletonloading(true);
      await getOrderLogsData(orderId.value);
      setSkeletonloading(false);
      event.target.complete();
    };

    const timeLineDot = (orderStatus) => {
      const redStatuses = [ORDER_STATUS_NAME.CANCELLED, ORDER_STATUS_NAME.FAILED_DELIVERY];
      return redStatuses.includes(orderStatus) ? 'time-line-red' : 'time-line-green';
    };

    const timeLine = (orderStatus) => {
      const redStatuses = [ORDER_STATUS_NAME.CANCELLED, ORDER_STATUS_NAME.FAILED_DELIVERY];
      return redStatuses.includes(orderStatus) ? 'red' : 'green';
    };

    const orderStatusInfo = (item) => {
      let orderStatus = item.status;
      let isCancelledBySupplier = USER_TYPE.SALE.includes(item.changed_by_user_type);

      switch (true) {
        // Set status that is order or booking
        case orderStatus === ORDER_STATUS_NAME.CANCELLED && !isCancelledBySupplier:
          return t('has_been_cancelled', {
            isOrderBooking: isBookingOrder.value ? t('booking_lowcase') : t('order_lowcase')
          });
        case orderStatus === ORDER_STATUS_NAME.COMPLETED:
          return isBookingOrder.value ? t('booking_has_been_confirmed') : t('order_has_been_completed');
        case orderStatus === ORDER_STATUS_NAME.PROCESSED:
          return (
            item.changed_by +
            ' ' +
            t('is_preparing_order', {
              isOrderBooking: isBookingOrder.value ? t('booking_lowcase') : t('order_lowcase')
            })
          );
        case orderStatus === ORDER_STATUS_NAME.FAILED_DELIVERY:
          return t('delivery_failed');
        case orderStatus === ORDER_STATUS_NAME.DELIVERY_SUCCEED ||
          orderStatus === ORDER_STATUS_NAME.DELIVERY_SUCCESSFUL:
          return t('delivered_success', {
            isOrderBooking: isBookingOrder.value ? t('booking_lowcase') : t('order_lowcase')
          });
        case orderStatus === ORDER_STATUS_NAME.DELIVERY_STARTED:
          return t('has_pickup_order', {
            isOrderBooking: isBookingOrder.value ? t('booking_lowcase') : t('order_lowcase')
          });
        case orderStatus === ORDER_STATUS_NAME.CANCELLED && isCancelledBySupplier:
          return t('cancelled_by_supplier', {
            isOrderBooking: isBookingOrder.value ? t('booking_lowcase') : t('order_lowcase'),
            supplierName: item.changed_by
          });
        case orderStatus === ORDER_STATUS_NAME.BLOCKED:
          return t('order_is_on_hold');
        case orderStatus === ORDER_STATUS_NAME.AMENDED:
          return t('order_has_been_amended', {
            isOrderBooking: isBookingOrder.value ? t('booking_lowcase') : t('order_lowcase')
          });
        default:
          return t('you_have_placed_order', {
            isOrderBooking: isBookingOrder.value ? t('booking_lowcase') : t('order_lowcase')
          });
      }
    };

    const orderInfoColor = (orderStatus) => {
      const redStatuses = [ORDER_STATUS_NAME.CANCELLED, ORDER_STATUS_NAME.FAILED_DELIVERY];
      return redStatuses.includes(orderStatus) ? 'text-red' : 'text-green';
    };

    const showOrderStatusNoteReason = (item) => {
      let noteAndReasonValue = noteAndReason(item);
      return (
        ([
          ORDER_STATUS_NAME.DELIVERY_SUCCEED,
          ORDER_STATUS_NAME.DELIVERY_SUCCESSFUL,
          ORDER_STATUS_NAME.FAILED_DELIVERY
        ].includes(item.status) ||
          (item.status === ORDER_STATUS_NAME.CANCELLED && item.reason !== ACTION_BY_MERCHANT)) &&
        noteAndReasonValue?.length > 0
      );
    };

    const updatedBy = (itemInfo) => {
      const {
        changed_by,
        changed_by_user_type,
        updated_by_first_name,
        updated_by_last_name,
        updated_by_user_id
      } = itemInfo;
      let userTypeId;
      const isChangeBySupplier =
        changed_by_user_type === 5 ||
        changed_by_user_type === 2 ||
        changed_by_user_type === 7 ||
        (changed_by_user_type === 4 && changed_by?.length > 0);

      switch (true) {
        // show update by "you" only for merchants user
        case userInfo.value?.user_type_id === 6 && userInfo.value?.id === updated_by_user_id:
          return t('update_type.you');

        case userType.value === 'b2b' && isChangeBySupplier:
          return t('update_type.supplier');

        case [2, 4, 5, 6, 7].includes(changed_by_user_type):
          userTypeId =
            changed_by_user_type === 6
              ? t('update_type.merchants')
              : changed_by_user_type === 7
              ? t('update_type.sales')
              : t('update_type.admin');
          return t('update_by_user', {
            userName: updated_by_first_name + ' ' + updated_by_last_name,
            userType: userTypeId,
            userId: updated_by_user_id
          });

        default:
          return t('update_type.system');
      }
    };

    const showAmended = (status) => {
      return status === ORDER_STATUS_NAME.AMENDED;
    };

    // set note or reason value
    const noteAndReason = (noteReasonParams) => {
      let { reason_type, reason } = noteReasonParams;
      let noteReason = '';

      reason = reason?.trim();
      noteReason = reason ? (reason_type ? `${reason_type}. ${reason}` : reason) : reason_type;
      return noteReason;
    };

    const titleNoteReason = (orderStatus) => {
      switch (orderStatus) {
        case ORDER_STATUS_NAME.CANCELLED:
          return t('cancellation_reason');
        case ORDER_STATUS_NAME.FAILED_DELIVERY:
          return t('failure_reason');
        default:
          return t('note');
      }
    };

    const showDriverMapLocation = (orderStatus) => {
      return orderStatus === ORDER_STATUS_NAME.DELIVERY_STARTED;
    };

    const lastUpdateDateTime = (orderDateTimeUpdate) => {
      return dayjs(orderDateTimeUpdate).format('ddd, D MMMM YYYY hh:mm A');
    };

    const checkEmptyLink = (trackingLink) => {
      return trackingLink.trim() === '';
    };

    const isBookingOrder = computed(() => {
      return orderStatusLogs.value?.is_booking;
    });
    const setOrderBookingHeader = computed(() => {
      return showSkeletonLoading.value
        ? ''
        : !showSkeletonLoading.value && !isBookingOrder.value
        ? t('track_order')
        : t('track_booking');
    });
    return {
      chevronBackOutline,
      eyeOutline,
      route,
      router,
      showSkeletonLoading,
      setSkeletonloading,
      orderStatusLogs,
      handleRefresh,
      orderId,
      userInfo,
      userType,
      isConnectionIssue,
      isOrderIdNotFound,
      trackingLinkUrl,
      isOpenDriverLocation,
      isOpenPreviewImages,
      isBooking,
      imagesData,
      setTrackDriverLocation,
      setOpenImages,
      closeTrackDriverPage,
      isBackFromTrackDriver,
      clearIframeHistoryStateEffect,
      goBack,
      getOrderLogsData,
      timeLineDot,
      timeLine,
      orderStatusInfo,
      orderInfoColor,
      titleNoteReason,
      updatedBy,
      showAmended,
      showDriverMapLocation,
      lastUpdateDateTime,
      showOrderStatusNoteReason,
      noteAndReason,
      checkEmptyLink,
      isBookingOrder,
      setOrderBookingHeader
    };
  },

  async mounted() {
    this.setSkeletonloading(true);
    this.orderId = this.$route.params.order_id;
    const currentPathUrl = window.location.pathname;
    const pathSegments = currentPathUrl.split('/');
    this.userType = pathSegments[1];
    await this.getOrderLogsData(this.orderId);
    this.userInfo = await this.$storage.getUser();
  }
});
</script>

<style src="./style.scss" lang="scss" scoped></style>
